import { Review } from './Review';
import { ComponentConfig } from '@measured/puck';
import {
  MarginProps,
  marginFields,
} from '../../../page-builder/puck/reusable-props/margin';
import { IAsset } from '../../interface/apiServiceInterface';
import { v4 as uuidv4 } from 'uuid';
import Button from '../../../components/common/button';
import CustomTooltip from '../../../components/common/tooltip';
import { CustomizeProps } from '../../interface/commonInterface';
import TextCustomInput from '../../puck/reusable-props/TextCustomInput';
import { ReduxSelectorProps } from '../../services/constants';
import {
  ImageProps,
  imageProps,
} from '../../../page-builder/puck/reusable-props/image-props';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import {
  defaultImage,
  ImageSelector,
} from '../../../components/common/image-selector/ImageSelector';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import { AxiosInstance } from 'axios';
import Border, {
  IBorderValueProps,
  borderProps,
} from '../../../page-builder/puck/reusable-props/Border';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
} from '../../puck/reusable-props/padding';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';

interface ReviewData {
  id: string;
  name: string;
  image: IAsset;
  review: string;
  maxWidth: number;
  height: number;
}

export interface CustomerReviewsProps
  extends MarginProps,
    PaddingProps,
    showComponentProps,
    ImageProps {
  reviews: ReviewData[];
  hideImage: boolean;
  dividerLine: boolean;
  hideVerifiedReview: boolean;
  border: IBorderValueProps;
  starRating: boolean;
  Header: CustomizeProps;
  background: {
    color: string;
    isInherit: boolean;
  };
}

export interface CustomerProps extends ReduxSelectorProps {
  baseService: AxiosInstance;
}

export const CustomerReviews = ({
  baseService,
  useAppSelector,
}: CustomerProps): ComponentConfig<CustomerReviewsProps> => {
  return {
    label: (
      <CustomTooltip title="CustomerReviews">
        <span>Customer Reviews</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...marginFields,
      ...paddingFields,
      ...backgroungFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      reviews: {
        type: 'custom',
        label: 'Reviews',
        render: ({ value, onChange }) => {
          value = value || [];

          const handleAddReview = () => {
            const newReview: ReviewData = {
              id: uuidv4(),
              name: '',
              image: defaultImage,
              review: '',
              maxWidth: 100,
              height: 100,
            };
            onChange([newReview, ...value]);
          };

          const handleEditReview = (
            index: number,
            key: keyof ReviewData,
            newValue: IAsset | string,
          ) => {
            onChange(
              value.map((review: ReviewData, i: number) =>
                i === index ? { ...review, [key]: newValue } : review,
              ),
            );
          };

          const handleRemoveReview = (index: number) => {
            onChange(value.filter((_: unknown, i: number) => i !== index));
          };

          return (
            <div>
              <Button
                label="+ Add new review"
                className="mt-4 w-full"
                variant="phoenix"
                onClick={handleAddReview}
              />
              {value.map((review: ReviewData, index: number) => (
                <div key={review.id} className="mt-4">
                  <ImageSelector
                    handleSubmit={selectedImage => {
                      handleEditReview(index, 'image', selectedImage);
                    }}
                    selectedImage={review.image}
                    baseService={baseService}
                  />
                  <input
                    className="mt-5 bg-white border border-gray-300 rounded px-4 py-3 text-sm w-full transition-colors duration-50 ease-in pagebuilder-theme"
                    placeholder="Name"
                    value={review.name}
                    onChange={e =>
                      handleEditReview(index, 'name', e.target.value)
                    }
                  />
                  <textarea
                    className="mt-5 bg-white border border-gray-300 rounded px-4 py-3 text-sm w-full transition-colors duration-50 ease-in pagebuilder-theme"
                    placeholder="Review"
                    value={review.review}
                    onChange={e =>
                      handleEditReview(index, 'review', e.target.value)
                    }
                  />
                  <Button
                    label="- Remove Review"
                    className="mt-4 w-full"
                    variant="phoenix_secondary"
                    onClick={() => handleRemoveReview(index)}
                  />
                </div>
              ))}
            </div>
          );
        },
      },
      Header: {
        type: 'custom',
        label: 'Header',
        render: ({ value, onChange, field }) => (
          <TextCustomInput
            value={value}
            showComponent={true}
            onChange={onChange}
            title={field?.label}
          />
        ),
      },
      ...imageProps([]),
      hideVerifiedReview: {
        type: 'radio',
        label: 'Hide Verified Buyer',
        options: [
          { value: true, label: 'True' },
          { value: false, label: 'False' },
        ],
      },
      starRating: {
        type: 'radio',
        label: 'Star Rating Position',
        options: [
          { value: true, label: 'Top' },
          { value: false, label: 'Bottom' },
        ],
      },
      dividerLine: {
        type: 'radio',
        label: 'Divider Line',
        options: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
      },
      hideImage: {
        type: 'radio',
        label: 'Hide Image',
        options: [
          { value: true, label: 'True' },
          { value: false, label: 'False' },
        ],
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      dividerLine: true,
      Header: {
        weight: {
          mobile: 'font-normal',
          tablet: 'font-normal',
          desktop: 'font-normal',
        },
        showComponent: {
          mobile: true,
          tablet: true,
          desktop: true,
        },
        deviceType: 'desktop',
        text: 'Customer Reviews',
        textColor: {
          mobile: '#000000',
          tablet: '#000000',
          desktop: '#000000',
        },
        fontSize: {
          mobile: 'text-sm',
          tablet: 'text-sm',
          desktop: 'text-sm',
        },
      },
      image: {
        height: {
          desktop: '100',
          mobile: '100',
          tablet: '100',
        },
        maxWidth: {
          desktop: '100',
          mobile: '100',
          tablet: '100',
        },
        deviceType: 'desktop',
      },
      reviews: [],
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      ...paddingProps,
      hideImage: false,
      ...borderProps,
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      hideVerifiedReview: false,
      starRating: true,
    },
    render: ({
      reviews,
      Header,
      margin,
      padding,
      showComponent,
      image,
      hideImage,
      border,
      background,
      hideVerifiedReview,
      dividerLine,
      starRating,
      editMode,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);

      const state = useAppSelector(state => state);
      const newHeader = {
        ...Header,
        text: getQuillEditorText(Header?.text, state),
      };
      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              style={{
                backgroundColor: background?.isInherit
                  ? 'inherit'
                  : background?.color,
                ...getBorderProps(border, currentDeviceType),
                ...marginStyles,
                ...getPaddingStyles(padding, currentDeviceType),
              }}
              className={`pt-10 pb-5  ${border?.borderStyle?.[currentDeviceType]} `}>
              {newHeader?.showComponent?.[currentDeviceType] && (
                <div className="flex items-center justify-center">
                  {dividerLine && (
                    <div className="flex-grow border-b border-black mx-2" />
                  )}
                  <p
                    id="contact-info-heading"
                    className={`${newHeader.weight?.[currentDeviceType]} ${newHeader.fontSize?.[currentDeviceType]} text-center`}
                    style={{
                      color: newHeader?.textColor?.[currentDeviceType],
                    }}>
                    {newHeader?.text}
                  </p>

                  {dividerLine && (
                    <div className="flex-grow border-b border-black mx-2" />
                  )}
                </div>
              )}
              {reviews && reviews.length === 0 && (
                <div className="mt-10 text-center text-lg text-gray-600">
                  No reviews yet
                </div>
              )}
              {reviews?.map(r => (
                <Review
                  key={r?.id}
                  name={r?.name}
                  review={r?.review}
                  image={r?.image}
                  height={image?.height}
                  maxWidth={image?.maxWidth}
                  currentDeviceType={currentDeviceType}
                  hideImage={hideImage}
                  hideVerifiedReview={hideVerifiedReview}
                  starRating={starRating}
                />
              ))}
            </div>
          )}
        </>
      );
    },
  };
};
